// ─── React & Dependencies ───────────────────────────────────────
import React, { useCallback } from "react";

// ─── Styles ─────────────────────────────────────────────────────
import styles from "./styles.module.scss";

// ─── Helpers ───────────────────────────────────────────────────

import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  formatAmount,
  formatDate,
  formatTime,
} from "../../../../setup/util/wallet";

// ─── Types ──────────────────────────────────────────────────────
interface WithdrawalHistoryItemProps {
  withdrawal: {
    referenceNumber: string;
    toAccount: string;
    withdrawalNumber: string;
    amount: number;
    histories: Array<{
      date: string;
      status: string;
    }>;
  };
  onClick?: () => void;
  showMobileView?: boolean;
}

const WithdrawalHistoryItem = ({
  withdrawal,
  onClick,
}: WithdrawalHistoryItemProps) => {
  // Get the latest history entry for status and date
  const latestHistory = withdrawal.histories[withdrawal.histories?.length - 1];
  const { t } = useTranslation();

  // Determine which icon to show based on status
  const getStatusIcon = () => {
    const status = latestHistory?.status?.toLowerCase() || "";
    if (status.includes("completed") || status.includes("success")) {
      return "/assets/images/svg/wallet_withdraw_completed.svg";
    }
    return "/assets/images/svg/wallet_in_progress.svg";
  };

  // Determine status and icon
  const isCompleted =
    latestHistory?.status?.toLowerCase().includes("completed") ||
    latestHistory?.status?.toLowerCase().includes("success");

  // Render timeline item for each history entry
  const renderTimelineItem = useCallback(
    (history: any, index: number) => {
      // Determine what to show based on the history entry
      let title = "";
      let amount = "";
      let amountColor = "";

      if (index === 0) {
        // First entry - withdrawal requested
        title = t("balance_bulk_listing_requested");
        amount = formatAmount(withdrawal.amount);
        amountColor = "#0D44AD";
      } else if (isCompleted && index === 1) {
        // Second entry - successful transfer (only if completed)
        title = `${t("balance_withdraw_subtitle", {
          val: withdrawal?.referenceNumber,
        })}`;
        amount = `-${formatAmount(withdrawal.amount)}`;
        amountColor = "#E32A34";
      }

      return (
        <div
          key={index}
          className={classNames(styles["timeline_item"], {
            [styles["first_item"]]: index === 0,
          })}>
          <div className={styles["item_container"]}>
            <div className={styles["item_title"]}>
              <p className={styles["f18"]}>{title}</p>
              <div className={styles["thread"]} />
            </div>
            {amount && (
              <div className={styles["details_container"]}>
                <p className={styles["price"]} style={{ color: amountColor }}>
                  {amount} {t("lbl_saudi_currency")}
                </p>
                <div className={styles["data_cont"]}>
                  <div className={styles["dot"]} />
                  <p className={styles["time"]}>{formatDate(history.date)}</p>
                  <img
                    src="assets/images/svg/hollow-clock-circle.svg"
                    alt="clock"
                    className={styles["clock_icon"]}
                  />
                  <p className={styles["time"]}>{formatTime(history.date)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    },
    [isCompleted, t, withdrawal?.amount, withdrawal?.referenceNumber]
  );

  return (
    <div className={styles.withdrawal_item} onClick={onClick}>
      {/* Right Section - Operation Details and Status */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
        <img
          src={getStatusIcon()}
          className={styles.status_icon}
          alt="status"
        />

        <div>
          <div className={styles.right_section}>
            <div className={styles.operation_container}>
              <div className={styles.operation_text_container}>
                <p className={styles.operation_text}>
                  {t("balance_withdraw_title", {
                    val1: withdrawal?.withdrawalNumber,
                    val2: `${withdrawal?.toAccount}`,
                    val3: "تحويل بنكي",
                  })}
                </p>
              </div>
            </div>

            {/* Transaction timeline with right-side line and threads */}
            <div className={styles["timeline_container"]}>
              {/* Right-side vertical line */}
              <div className={styles["timeline_right_section"]}>
                <div className={styles["line"]} />
              </div>

              {/* Timeline items */}
              <div className={styles["timeline"]}>
                {/* Render each history entry */}
                {withdrawal.histories.map((history, index) =>
                  renderTimelineItem(history, index)
                )}

                {/* Status timeline item - only show if there's just one history element */}
                {withdrawal.histories.length === 1 && (
                  <div className={styles["timeline_item"]}>
                    <div className={styles["item_container"]}>
                      <div className={styles["item_title"]}>
                        <p
                          className={styles["status_text"]}
                          style={{ color: "#D7A819" }}>
                          {t("balance_bulk_listing_processing")}
                        </p>
                        <div className={styles["thread"]} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalHistoryItem;
